import { CodeEditor } from "@/components/CodeEditor";
import type { OnMount } from "@monaco-editor/react";
import type { Language } from "@/lib/types";

type MonacoEditor = Parameters<OnMount>[0];
type Monaco = Parameters<OnMount>[1];

interface Props {
  filename: string;
  language: Language;
  value: string;
  onChange: (v: string) => void;
  onMount?: (editor: MonacoEditor, monaco: Monaco) => void;
  markers: { errors: number; warnings: number };
  onMarkersChange: (m: { errors: number; warnings: number }) => void;
  onReset: () => void;
  /** Altura del editor en px. Monaco necesita altura explícita (no flex). */
  editorHeight?: number;
}

/**
 * Wrapper estilo VS Code / macOS:
 *   - 3 dots de colores en el title bar
 *   - Nombre del archivo (font-mono)
 *   - Status TS (verde / amarillo / rojo) + Reset link
 *   - Editor abajo con altura fija (Monaco no se layout-ea bien dentro de flex)
 */
export function EditorWindow({
  filename,
  language,
  value,
  onChange,
  onMount,
  markers,
  onMarkersChange,
  onReset,
  editorHeight = 520,
}: Props) {
  const hasErrors = markers.errors > 0;
  const hasWarnings = !hasErrors && markers.warnings > 0;

  return (
    <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
      {/* Title bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border bg-secondary/20">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
            <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
            <span className="h-3 w-3 rounded-full bg-[#28c840]" />
          </div>
          <span className="text-xs font-mono text-muted-foreground">{filename}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <TsStatus hasErrors={hasErrors} hasWarnings={hasWarnings} markers={markers} />
          <button
            type="button"
            onClick={onReset}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Editor — altura fija para que Monaco se renderice correctamente */}
      <CodeEditor
        language={language}
        value={value}
        onChange={onChange}
        height={editorHeight}
        onMount={onMount}
        onMarkersChange={onMarkersChange}
        minimap
        className="rounded-none border-0"
      />
    </div>
  );
}

function TsStatus({
  hasErrors,
  hasWarnings,
  markers,
}: {
  hasErrors: boolean;
  hasWarnings: boolean;
  markers: { errors: number; warnings: number };
}) {
  if (hasErrors) {
    return (
      <span className="text-destructive inline-flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
        {markers.errors} error{markers.errors !== 1 ? "es" : ""}
        {markers.warnings > 0 && `, ${markers.warnings} warning${markers.warnings !== 1 ? "s" : ""}`}
      </span>
    );
  }
  if (hasWarnings) {
    return (
      <span className="text-warning inline-flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-warning" />
        {markers.warnings} warning{markers.warnings !== 1 ? "s" : ""}
      </span>
    );
  }
  return (
    <span className="text-success inline-flex items-center gap-1.5">
      <span className="h-1.5 w-1.5 rounded-full bg-success" />
      Sin errores TypeScript
    </span>
  );
}

